/**
 * Settings API
 *
 * API endpoints for settings management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type { GetSettingsByCompanyArgs, SettingsByCompany, UpdateSettingsArgs, UploadLogoSettings, UploadLogoSettingsArgs } from "./settings.types";

/**
 * Settings API endpoints
 * Provides operations for settings management
 */
export const settingsApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get settings by company ID
     * @param id - Company ID
     * @returns Settings details
     */
    getSettingsByCompany: build.query<SettingsByCompany, GetSettingsByCompanyArgs>({
      query: ({ id }) => `/settings/company/${id}`,
      transformResponse: (response: SettingsByCompany) => ({
        ...response,
        googleMaps: response.googleMaps.toLowerCase()
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando configurações...",
          success: "Configurações carregadas com sucesso!",
          error: "Erro ao carregar configurações."
        });
      }
    }),

    /**
     * Update settings
     * @param payload - Settings data to update
     * @returns void
     */
    updateSettings: build.mutation<SettingsByCompany, UpdateSettingsArgs>({
      query: (body) => ({
        url: `/settings/save`,
        method: "POST",
        body,
        responseHandler: "text"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando configurações...",
          success: "Configurações salvas com sucesso!",
          error: "Erro ao salvar configurações."
        });
      }
    }),

    /**
     * Upload logo for settings
     * @param logo - Logo file
     * @param id - Company ID
     * @returns Upload result
     */
    uploadLogoSettingsByCompany: build.mutation<UploadLogoSettings, UploadLogoSettingsArgs>({
      query: (body) => {
        const formData = new FormData();
        formData.append("logo", body.logo);
        formData.append("company", body.id.toString());

        return {
          url: "/settings/upload",
          method: "POST",
          body: formData
        };
      },
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Enviando logo...",
          success: "Logo enviada com sucesso!",
          error: "Erro ao enviar logo."
        });
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
settingsApi.enhanceEndpoints({
  addTagTypes: ["Settings", "SettingsByCompany"],
  endpoints: {
    getSettingsByCompany: {
      providesTags: (result, error, { id }) => [{ type: "SettingsByCompany", id }]
    },
    updateSettings: {
      invalidatesTags: (result, error, { id }) => {
        if (!error)
          return [
            { type: "SettingsByCompany", id },
            { type: "Settings", id }
          ];
        return [];
      }
    },
    uploadLogoSettingsByCompany: {
      invalidatesTags: (result, error, { id }) => {
        if (!error)
          return [
            { type: "SettingsByCompany", id },
            { type: "Settings", id }
          ];
        return [];
      }
    }
  }
});
